import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();

export interface WarehouseRecord {
  uuid: string;
  code: string;
  name: string;
  managerMemberUuid?: string;
  phone?: string;
  address?: string;
  isDefault: boolean;
  isEnabled: boolean;
}

export interface CategoryRecord {
  uuid: string;
  code: string;
  name: string;
  parentUuid?: string;
  isEnabled: boolean;
}

export interface BrandRecord {
  uuid: string;
  code: string;
  name: string;
  isEnabled: boolean;
}

export interface UnitRecord {
  uuid: string;
  code: string;
  name: string;
  isEnabled: boolean;
}

export interface SupplierRecord {
  uuid: string;
  code: string;
  name: string;
  contactName?: string;
  phone?: string;
  address?: string;
  isEnabled: boolean;
  score: string;
  level: string;
  riskLevel: string;
  lastEvaluatedAt?: string;
  evaluationSummary?: string;
  disabledReason?: string;
}

export interface MaterialRecord {
  uuid: string;
  code: string;
  name: string;
  categoryUuid: string;
  brandUuid?: string;
  unitUuid: string;
  defaultWarehouseUuid: string;
  safetyStock: string;
  isSpecial: boolean;
  isFixedAsset: boolean;
  status: string;
}

export interface StockRecord {
  uuid: string;
  warehouseUuid: string;
  warehouseName?: string;
  materialUuid: string;
  materialCode?: string;
  materialName?: string;
  categoryUuid?: string;
  brandUuid?: string;
  currentQty: string;
  frozenQty: string;
  inTransitQty: string;
  availableQty: number;
  safetyStock: number;
  averageCost: string;
  inventoryValue: string;
  status: string;
  healthScore: number;
  warningReasons: string[];
  updatedAt: string;
}

export interface StockWarningRecord {
  uuid: string;
  warningNo: string;
  stockUuid: string;
  warehouseUuid: string;
  materialUuid: string;
  warningType: string;
  warningLevel: string;
  content: string;
  status: string;
  currentQty: string;
  availableQty: string;
  safetyStock: string;
  suggestPurchaseQty: string;
  purchaseSuggestionNo?: string;
  handledAt?: string;
  handleComment?: string;
  createdAt: string;
}

export interface StockDetailRecord extends StockRecord {
  material?: MaterialRecord;
  warehouse?: WarehouseRecord;
  recentFlows: StockFlowRecord[];
  warnings: StockWarningRecord[];
}

export interface StockFlowRecord {
  uuid: string;
  flowNo: string;
  stockUuid: string;
  warehouseUuid: string;
  materialUuid: string;
  flowType: string;
  changeQty: string;
  beforeQty: string;
  afterQty: string;
  reason?: string;
  remark?: string;
  createdAt: string;
}

export interface StockAdjustmentRecord {
  uuid: string;
  adjustNo: string;
  warehouseUuid: string;
  materialUuid: string;
  stockUuid?: string;
  beforeQty: string;
  changeQty: string;
  afterQty: string;
  flowType: string;
  reason: string;
  remark?: string;
  status: string;
  ruleResult?: string;
  ruleExplanation?: string;
  stockFlowNo?: string;
  applicantMemberUuid: string;
  submittedAt?: string;
  approvedAt?: string;
  finishedAt?: string;
  createdAt: string;
}

export interface StockTransferRecord {
  uuid: string;
  transferNo: string;
  sourceWarehouseUuid: string;
  targetWarehouseUuid: string;
  transferType: string;
  status: string;
  totalTransferQty: string;
  totalReceivedQty: string;
  totalDiffQty: string;
  totalAmount: string;
  applicantMemberUuid: string;
  outboundMemberUuid?: string;
  inboundMemberUuid?: string;
  reason: string;
  remark?: string;
  submittedAt?: string;
  outboundAt?: string;
  inboundAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  createdAt: string;
}

export interface StockTransferItemRecord {
  uuid: string;
  transferUuid: string;
  materialUuid: string;
  materialCodeSnapshot?: string;
  materialNameSnapshot?: string;
  sourceStockUuid?: string;
  targetStockUuid?: string;
  transferQty: string;
  receivedQty: string;
  diffQty: string;
  unitCost: string;
  transferAmount: string;
  outFlowNo?: string;
  inFlowNo?: string;
  diffReason?: string;
  remark?: string;
}

export interface StockTransferDetailRecord {
  order: StockTransferRecord;
  items: StockTransferItemRecord[];
}

export interface CreateStockAdjustmentPayload {
  warehouseUuid: string;
  materialUuid: string;
  quantity: number;
  flowType: 'adjustment' | 'check_profit' | 'check_loss';
  reason: string;
  remark?: string;
}

export interface CreateStockTransferPayload {
  sourceWarehouseUuid: string;
  targetWarehouseUuid: string;
  transferType?: 'normal' | 'replenishment' | 'emergency';
  reason: string;
  remark?: string;
  items: Array<{
    materialUuid: string;
    transferQty: number;
    remark?: string;
  }>;
}

export interface ConfirmStockTransferInPayload {
  remark?: string;
  items?: Array<{
    transferItemUuid: string;
    receivedQty: number;
    diffReason?: string;
  }>;
}

export interface CreateWarehousePayload {
  name: string;
  managerMemberUuid?: string;
  phone?: string;
  address?: string;
  isDefault?: boolean;
}

export interface CreateSupplierPayload {
  name: string;
  contactName?: string;
  phone?: string;
  address?: string;
}

export interface CreateMaterialPayload {
  name: string;
  categoryUuid: string;
  brandUuid?: string;
  unitUuid: string;
  defaultWarehouseUuid: string;
  safetyStock: number;
  isSpecial: boolean;
  isFixedAsset: boolean;
}

export async function getCategories(options?: ApiRequestOptions) {
  return request<CategoryRecord[]>('/bff/inventory/categories', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createCategory(
  data: { name: string; parentUuid?: string },
  options?: ApiRequestOptions,
) {
  return request<CategoryRecord>('/bff/inventory/categories', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getCategoryOptions(
  categoryUuid: string,
  options?: ApiRequestOptions,
) {
  return request<{ unitUuids: string[]; brandUuids: string[] }>(
    `/bff/inventory/categories/${categoryUuid}/options`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function updateCategoryOptions(
  categoryUuid: string,
  data: { unitUuids: string[]; brandUuids: string[] },
  options?: ApiRequestOptions,
) {
  return request<{ unitUuids: string[]; brandUuids: string[] }>(
    `/bff/inventory/categories/${categoryUuid}/options`,
    {
      method: 'PUT',
      data,
      ...(options || {}),
    },
  );
}

export async function getBrands(options?: ApiRequestOptions) {
  return request<BrandRecord[]>('/bff/inventory/brands', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createBrand(data: { name: string }, options?: ApiRequestOptions) {
  return request<BrandRecord>('/bff/inventory/brands', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getUnits(options?: ApiRequestOptions) {
  return request<UnitRecord[]>('/bff/inventory/units', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createUnit(data: { name: string }, options?: ApiRequestOptions) {
  return request<UnitRecord>('/bff/inventory/units', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getWarehouses(options?: ApiRequestOptions) {
  return request<WarehouseRecord[]>('/bff/inventory/warehouses', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createWarehouse(
  data: CreateWarehousePayload,
  options?: ApiRequestOptions,
) {
  return request<WarehouseRecord>('/bff/inventory/warehouses', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getSuppliers(options?: ApiRequestOptions) {
  return request<SupplierRecord[]>('/bff/inventory/suppliers', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createSupplier(
  data: CreateSupplierPayload,
  options?: ApiRequestOptions,
) {
  return request<SupplierRecord>('/bff/inventory/suppliers', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function evaluateSupplier(
  supplierUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<SupplierRecord>(`/bff/inventory/suppliers/${supplierUuid}/evaluate`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getMaterials(options?: ApiRequestOptions) {
  return request<MaterialRecord[]>('/bff/inventory/materials', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createMaterial(
  data: CreateMaterialPayload,
  options?: ApiRequestOptions,
) {
  return request<MaterialRecord>('/bff/inventory/materials', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function updateMaterial(
  materialUuid: string,
  data: Partial<MaterialRecord>,
  options?: ApiRequestOptions,
) {
  return request<MaterialRecord>(`/bff/inventory/materials/${materialUuid}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function disableMaterial(materialUuid: string, options?: ApiRequestOptions) {
  return request<MaterialRecord>(`/bff/inventory/materials/${materialUuid}/disable`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function getStocks(options?: ApiRequestOptions) {
  return request<StockRecord[]>('/bff/inventory/stocks', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getStockDetail(stockUuid: string, options?: ApiRequestOptions) {
  return request<StockDetailRecord>(`/bff/inventory/stocks/${stockUuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getStockWarnings(options?: ApiRequestOptions) {
  return request<StockRecord[]>('/bff/inventory/stocks/warnings', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getStockWarningRecords(options?: ApiRequestOptions) {
  return request<StockWarningRecord[]>('/bff/inventory/stock-warnings', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function handleStockWarning(
  warningUuid: string,
  data: { status: string; purchaseSuggestionNo?: string; comment?: string },
  options?: ApiRequestOptions,
) {
  return request<StockWarningRecord>(`/bff/inventory/stock-warnings/${warningUuid}/handle`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function freezeStock(
  data: {
    warehouseUuid: string;
    materialUuid: string;
    quantity: number;
    sourceNo?: string;
    reason?: string;
    remark?: string;
  },
  options?: ApiRequestOptions,
) {
  return request<StockRecord>('/bff/inventory/stocks/freeze', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function unfreezeStock(
  data: {
    warehouseUuid: string;
    materialUuid: string;
    quantity: number;
    sourceNo?: string;
    reason?: string;
    remark?: string;
  },
  options?: ApiRequestOptions,
) {
  return request<StockRecord>('/bff/inventory/stocks/unfreeze', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function getStockFlows(stockUuid: string, options?: ApiRequestOptions) {
  return request<StockFlowRecord[]>(`/bff/inventory/stocks/${stockUuid}/flows`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateWarehouse(
  warehouseUuid: string,
  data: Partial<WarehouseRecord>,
  options?: ApiRequestOptions,
) {
  return request<WarehouseRecord>(`/bff/inventory/warehouses/${warehouseUuid}`, {
    method: 'PUT',
    data,
    ...(options || {}),
  });
}

export async function disableWarehouse(warehouseUuid: string, options?: ApiRequestOptions) {
  return request<WarehouseRecord>(`/bff/inventory/warehouses/${warehouseUuid}/disable`, {
    method: 'POST',
    ...(options || {}),
  });
}

export async function getStockAdjustments(options?: ApiRequestOptions) {
  return request<StockAdjustmentRecord[]>('/bff/inventory/stock-adjustments', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function createStockAdjustment(
  data: CreateStockAdjustmentPayload,
  options?: ApiRequestOptions,
) {
  return request<StockAdjustmentRecord>('/bff/inventory/stock-adjustments', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function approveStockAdjustment(
  adjustUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<StockAdjustmentRecord>(
    `/bff/inventory/stock-adjustments/${adjustUuid}/approve`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

export async function rejectStockAdjustment(
  adjustUuid: string,
  data: { comment?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<StockAdjustmentRecord>(
    `/bff/inventory/stock-adjustments/${adjustUuid}/reject`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

export async function getStockTransfers(options?: ApiRequestOptions) {
  return request<StockTransferRecord[]>('/bff/inventory/stock-transfers', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getStockTransferDetail(
  transferUuid: string,
  options?: ApiRequestOptions,
) {
  return request<StockTransferDetailRecord>(
    `/bff/inventory/stock-transfers/${transferUuid}`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

export async function createStockTransfer(
  data: CreateStockTransferPayload,
  options?: ApiRequestOptions,
) {
  return request<StockTransferRecord>('/bff/inventory/stock-transfers', {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function outboundStockTransfer(
  transferUuid: string,
  options?: ApiRequestOptions,
) {
  return request<StockTransferRecord>(
    `/bff/inventory/stock-transfers/${transferUuid}/outbound`,
    {
      method: 'POST',
      data: {},
      ...(options || {}),
    },
  );
}

export async function inboundStockTransfer(
  transferUuid: string,
  data: ConfirmStockTransferInPayload = {},
  options?: ApiRequestOptions,
) {
  return request<StockTransferRecord>(
    `/bff/inventory/stock-transfers/${transferUuid}/inbound`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}

export async function cancelStockTransfer(
  transferUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request<StockTransferRecord>(
    `/bff/inventory/stock-transfers/${transferUuid}/cancel`,
    {
      method: 'POST',
      data,
      ...(options || {}),
    },
  );
}
